import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";

export type FlowControls = {
  push: (screen: FlowScreen) => void;
  pop: () => void;
  replace: (screen: FlowScreen) => void;
};

export type FlowScreen = {
  id: string;
  header?: ReactNode | ((flow: FlowControls) => ReactNode);
  headerHeight?: number;
  footer?: ReactNode | ((flow: FlowControls) => ReactNode);
  footerHeight?: number;
  render: (flow: FlowControls) => ReactNode;
};

export function FlowStack({ initial }: { initial: FlowScreen }) {
  const [stack, setStack] = useState<FlowScreen[]>([initial]);

  const push = useCallback((screen: FlowScreen) => {
    setStack((current) => [...current, screen]);
  }, []);
  const pop = useCallback(() => {
    setStack((current) => current.length > 1 ? current.slice(0, -1) : current);
  }, []);
  const replace = useCallback((screen: FlowScreen) => {
    setStack((current) => [...current.slice(0, -1), screen]);
  }, []);
  const flow = useMemo<FlowControls>(() => ({ push, pop, replace }), [pop, push, replace]);
  const current = stack[stack.length - 1];
  const header = typeof current.header === "function" ? current.header(flow) : current.header;
  const footer = typeof current.footer === "function" ? current.footer(flow) : current.footer;

  useEffect(() => {
    const handlePopState = () => pop();
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [pop]);

  useEffect(() => {
    const viewport = window.visualViewport;
    let frame = 0;
    let settleTimer = 0;
    const root = document.documentElement;
    const update = () => {
      frame = 0;
      const height = viewport?.height || window.innerHeight;
      const offsetTop = viewport?.offsetTop || 0;
      root.style.setProperty("--visual-viewport-height", `${Math.round(height)}px`);
      root.style.setProperty("--visual-viewport-top", `${Math.round(offsetTop)}px`);
      const active = document.activeElement;
      const editing = active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement;
      const keyboardOpen = editing && window.innerHeight - height > 120;
      if (keyboardOpen) root.style.setProperty("--mobile-safe-area-height", "0px");
      else root.style.removeProperty("--mobile-safe-area-height");
      // iOS pans the visual viewport as well as resizing it. Scroll only our
      // inner container; scrolling the document can pan the WebView a second time.
      if (editing && viewport && viewport.scale === 1) {
        const scroller = active.closest<HTMLElement>(".h5-scroll");
        if (scroller) {
          const rect = active.getBoundingClientRect();
          const bounds = scroller.getBoundingClientRect();
          const top = Math.max(bounds.top, offsetTop) + 12;
          const bottom = Math.min(bounds.bottom, offsetTop + height) - 12;
          if (rect.top < top) scroller.scrollTop += rect.top - top;
          else if (rect.bottom > bottom) scroller.scrollTop += Math.min(rect.bottom - bottom, rect.top - top);
        }
      }
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(update); };
    const focusChanged = () => {
      schedule();
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(schedule, 150);
    };
    update();
    viewport?.addEventListener("resize", schedule);
    viewport?.addEventListener("scroll", schedule);
    window.addEventListener("resize", schedule);
    document.addEventListener("focusin", focusChanged);
    document.addEventListener("focusout", focusChanged);
    return () => {
      viewport?.removeEventListener("resize", schedule);
      viewport?.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      document.removeEventListener("focusin", focusChanged);
      document.removeEventListener("focusout", focusChanged);
      window.cancelAnimationFrame(frame);
      window.clearTimeout(settleTimer);
      for (const property of ["--visual-viewport-height", "--visual-viewport-top", "--mobile-safe-area-height"]) root.style.removeProperty(property);
    };
  }, []);

  return (
    <div className="h5-flow" data-route={current.id}>
      {header ? <div className="h5-route-header" style={{ height: current.headerHeight }}>{header}</div> : null}
      <div className="h5-route-body" key={current.id}>{current.render(flow)}</div>
      {footer ? <div className="h5-route-footer" style={{ height: current.footerHeight }}>{footer}</div> : null}
    </div>
  );
}

export function MobileScroll({ className = "", children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={`h5-scroll ${className}`.trim()} {...props}>{children}</div>;
}

export const KeyboardInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function KeyboardInput(props, ref) {
    const composing = useRef(false);
    return <input ref={ref} {...props}
      onCompositionStart={(event)=>{composing.current=true;props.onCompositionStart?.(event);}}
      onCompositionEnd={(event)=>{composing.current=false;props.onCompositionEnd?.(event);}}
      onKeyDown={(event)=>{
        if(event.key==="Enter"&&(composing.current||event.nativeEvent.isComposing)){event.preventDefault();return;}
        props.onKeyDown?.(event);
      }}/>
  },
);

export const KeyboardTextarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function KeyboardTextarea(props, ref) {
    return <textarea ref={ref} {...props} />;
  },
);

export function Carousel({
  className = "",
  contentClassName = "",
  ariaLabel,
  children,
}: {
  className?: string;
  contentClassName?: string;
  ariaLabel?: string;
  children: ReactNode;
}) {
  return (
    <div className={`h5-carousel ${className}`.trim()} aria-label={ariaLabel}>
      <div className={`h5-carousel-content ${contentClassName}`.trim()}>{children}</div>
    </div>
  );
}

export function BottomSheet({
  open,
  onOpenChange,
  title,
  description,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  snap?: number;
  children?: ReactNode;
}) {
  const dialogRef = useRef<HTMLElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  useEffect(() => {
    if (!open) return;
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const scrollY = window.scrollY;
    requestAnimationFrame(() => dialogRef.current?.querySelector<HTMLElement>("button, a, [tabindex='0']")?.focus({ preventScroll: true }));
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChangeRef.current(false);
      if (event.key !== "Tab" || !dialogRef.current) return;
      const nodes = [...dialogRef.current.querySelectorAll<HTMLElement>("button:not(:disabled), a[href], [tabindex='0']")];
      if (!nodes.length) return;
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      returnFocusRef.current?.focus({ preventScroll: true });
      window.scrollTo({ top: scrollY });
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="h5-sheet-layer" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onOpenChange(false);
    }}>
      <section ref={dialogRef} className="h5-sheet" role="dialog" aria-modal="true" aria-labelledby="h5-sheet-title">
        <button className="h5-sheet-close" type="button" aria-label="关闭" onClick={() => onOpenChange(false)}>×</button>
        <span className="h5-sheet-handle" aria-hidden="true" />
        <h2 id="h5-sheet-title">{title}</h2>
        {description ? <p>{description}</p> : null}
        {children}
      </section>
    </div>
  );
}

const keyboard = {
  hide: () => {
    const active = document.activeElement;
    if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || (active instanceof HTMLElement && active.isContentEditable)) active.blur();
  },
};

export function useKeyboard() {
  return keyboard;
}
